import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import axios from '../services/api';
import { FiUploadCloud, FiX, FiImage } from 'react-icons/fi';
import { roundPrice } from '../utils/priceUtils';
import log from '../utils/logger';

const AuctionModal = ({ isOpen, onClose, onAuctionCreated, editMode = false, existingAuction = null }) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startingBid: '',
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  useEffect(() => {
    if (editMode && existingAuction) {
      setFormData({
        title: existingAuction.title || '',
        description: existingAuction.description || '',
        startingBid: existingAuction.startingBid || '',
      });
      setImagePreviews(existingAuction.images || []);
      // In edit mode, images array should contain the URLs for reordering
      setImages(existingAuction.images || []);
    }
  }, [editMode, existingAuction]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case 'title':
        if (!value.trim()) return 'Title is required';
        if (value.trim().length < 3) return 'Title must be at least 3 characters';
        if (value.length > 100) return 'Title must be under 100 characters';
        return '';
      case 'description':
        if (!value.trim()) return 'Description is required';
        if (value.trim().length < 10) return 'Description must be at least 10 characters';
        if (value.length > 500) return 'Description must be under 500 characters';
        return '';
      case 'startingBid':
        if (!value) return 'Starting bid is required';
        if (isNaN(value) || Number(value) < 0.01) return 'Starting bid must be at least $0.01';
        if (Number(value) >= 1000000) return 'Starting bid must be less than $1,000,000';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (error) setError(null);
    // Validate on change (clear error when fixed)
    const fieldError = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const fieldError = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const handleImageChange = (files) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const maxFiles = 10;
    
    let validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return false;
      }
      if (file.size > maxSize) {
        setError('Images must be smaller than 5MB');
        return false;
      }
      return true;
    });
    
    if (editMode) {
      // In edit mode, add new files to existing images
      const currentCount = images.length;
      if (currentCount + validFiles.length > maxFiles) {
        validFiles = validFiles.slice(0, maxFiles - currentCount);
        setError(`Maximum ${maxFiles} images allowed`);
      }
      const newImages = [...images, ...validFiles];
      const newPreviews = [...imagePreviews, ...validFiles.map(file => URL.createObjectURL(file))];
      setImages(newImages);
      setImagePreviews(newPreviews);
    } else {
      if (validFiles.length > maxFiles) {
        validFiles = validFiles.slice(0, maxFiles);
        setError(`Maximum ${maxFiles} images allowed`);
      }
      setImages(validFiles);
      setImagePreviews(validFiles.map(file => URL.createObjectURL(file)));
    }
    
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleImageChange(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...images];
    const newPreviews = [...imagePreviews];
    const [movedImage] = newImages.splice(fromIndex, 1);
    const [movedPreview] = newPreviews.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    newPreviews.splice(toIndex, 0, movedPreview);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  useEffect(() => {
    return () => imagePreviews.forEach(url => {
      // Only revoke blob URLs, not S3 HTTPS URLs
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }, [imagePreviews]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Validate all fields before submitting
    const errors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const err = validateField(key, String(value));
      if (err) errors[key] = err;
    });
    if (!editMode && images.length === 0) {
      errors.images = 'At least one image is required';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === 'startingBid') {
        data.append(key, roundPrice(value));
      } else {
        data.append(key, value);
      }
    });
    
    if (editMode) {
      // Send all images in order - URLs as strings, new files as files
      images.forEach((image, index) => {
        if (typeof image === 'string') {
          data.append(`imageUrl_${index}`, image);
        } else {
          data.append('images', image);
        }
      });
      data.append('totalImages', images.length.toString());
    } else {
      images.forEach(image => data.append('images', image));
    }
    
    data.append('email', user.email);

    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentage = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress({ overall: percentage });
      },
    };

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      
      if (editMode && existingAuction) {
        await axios.put(`/auctions/${existingAuction._id}`, data, config);
      } else {
        await axios.post('/auctions', data, config);
      }

      setSuccess(true);
      setTimeout(() => {
        setFormData({ title: '', description: '', startingBid: '' });
        setImages([]);
        setImagePreviews([]);
        setUploadProgress({});
        setSuccess(false);
        if (onAuctionCreated) onAuctionCreated();
        onClose();
      }, 2000);
    } catch (error) {
      log.error('Listing error:', error.response?.data || error.message);
      const serverMessage = error.response?.data?.message;
      setError(serverMessage || (editMode ? 'Failed to update auction. Please try again.' : 'Failed to create auction. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || isLoading || !isAuthenticated) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8">
        <div className="flex justify-between items-center p-6 border-b border-dark-border">
          <h2 className="text-2xl font-bold text-white">{editMode ? 'Edit Auction' : 'Create New Auction'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" autoComplete="off">
          {success && (
            <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg">
              {editMode ? 'Auction updated successfully!' : 'Auction created successfully! It will appear in the queue shortly.'}
            </div>
          )}
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-lg bg-dark-input border ${fieldErrors.title ? 'border-red-500' : 'border-dark-border'} text-white focus:ring-2 focus:ring-snipehouse-blue focus:border-snipehouse-blue transition-all placeholder-gray-500`}
                placeholder="Enter item title"
                minLength="3"
                maxLength="100"
                required
              />
              <div className="flex justify-between mt-1">
                {fieldErrors.title ? (
                  <p className="text-xs text-red-400">{fieldErrors.title}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-gray-500">{formData.title.length}/100</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-lg bg-dark-input border ${fieldErrors.description ? 'border-red-500' : 'border-dark-border'} text-white focus:ring-2 focus:ring-snipehouse-blue focus:border-snipehouse-blue h-32 resize-none transition-all placeholder-gray-500`}
                placeholder="Describe your item in detail..."
                minLength="10"
                maxLength="500"
                required
              />
              <div className="flex justify-between mt-1">
                {fieldErrors.description ? (
                  <p className="text-xs text-red-400">{fieldErrors.description}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-gray-500">{formData.description.length}/500</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Starting Bid ($)</label>
              <input
                name="startingBid"
                type="number"
                value={formData.startingBid}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-3 rounded-lg bg-dark-input border ${fieldErrors.startingBid ? 'border-red-500' : 'border-dark-border'} text-white focus:ring-2 focus:ring-snipehouse-blue focus:border-snipehouse-blue transition-all placeholder-gray-500`}
                placeholder="Enter starting bid"
                min="0.01"
                max="999999.99"
                step="0.01"
                required
              />
              {fieldErrors.startingBid && (
                <p className="text-xs text-red-400 mt-1">{fieldErrors.startingBid}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Images</label>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
                  ${dragActive ? 'border-snipehouse-blue bg-snipehouse-blue/10 scale-105' : 'border-dark-border hover:border-gray-600'}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <FiUploadCloud className="mx-auto text-gray-500" size={40} />
                  <div className="space-y-1">
                    <p className="font-medium text-gray-300">
                      {dragActive ? 'Drop images here' : 'Drag & drop photos here'}
                    </p>
                    {!dragActive && <p className="text-sm text-gray-500">or</p>}
                    <label className="inline-flex items-center px-4 py-2 bg-snipehouse-blue hover:bg-snipehouse-blue-dark text-white rounded-lg cursor-pointer transition-colors">
                      <FiImage className="mr-2" />
                      Browse Files
                      <input
                        type="file"
                        multiple
                        onChange={(e) => handleImageChange(e.target.files)}
                        className="hidden"
                        accept="image/*,.jpg,.jpeg,.png,.gif,.webp"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Up to 10 images, max 5MB each (JPEG, PNG, GIF, WebP)</p>
                </div>
              </div>

              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">Drag images to reorder • First image will be the main photo</p>
                  <div className="grid grid-cols-4 gap-4">
                    {imagePreviews.map((src, index) => (
                      <div 
                        key={index} 
                        className="relative group cursor-move"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                          moveImage(fromIndex, index);
                        }}
                      >
                        <div className="relative">
                          <img
                            src={src}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg shadow-sm group-hover:opacity-75 transition-opacity border border-dark-border"
                          />
                          {index === 0 && (
                            <div className="absolute top-2 left-2 bg-snipehouse-blue text-white text-xs px-2 py-1 rounded">
                              Main
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">Drag to reorder</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-sm hover:bg-red-600 transition-colors"
                        >
                          <FiX size={14} />
                        </button>
                        {uploadProgress.overall && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                            <div className="w-3/4 bg-dark-border rounded-full h-2">
                              <div
                                className="bg-snipehouse-blue h-2 rounded-full transition-all"
                                style={{ width: `${uploadProgress.overall}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-dark-border space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-300 hover:bg-dark-input rounded-lg transition-colors border border-dark-border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || (!editMode && images.length === 0) || !formData.title || !formData.description || !formData.startingBid}
                className="px-6 py-2 bg-snipehouse-blue hover:bg-snipehouse-blue-dark hover:scale-105 text-white rounded-lg transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-glow-blue-soft"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  editMode ? 'Update Auction' : 'List Auction Item'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

AuctionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAuctionCreated: PropTypes.func,
  editMode: PropTypes.bool,
  existingAuction: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    startingBid: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default AuctionModal;